import { normalizeSymbol } from '../chart/datafeed';
import { loadStoredJSON } from '../settings/storage';
import type { ApiQuote, ApiWatchlist, ChartSymbol } from '../types/api';
import type { AppTab, WatchlistQuote, WatchlistViewItem } from './AppShell.types';

export const activeTabStorageKey = 'app.activeTab';
export const chartStateStorageKey = 'app.chartState';
export const activeWatchlistStorageKey = 'app.activeWatchlistId';

export interface StoredChartState {
  symbol: string;
  interval: string;
}

export function quoteFromApi(quote: ApiQuote): WatchlistQuote {
  return {
    price: quote.price,
    change: quote.change,
    percent: quote.percent,
    updatedAt: quote.updated_at * 1000,
  };
}

export function shortSymbolName(symbol: string): string {
  return symbol.includes(':') ? symbol.split(':').pop() ?? symbol : symbol;
}

export function moveWatchlistItem(items: WatchlistViewItem[], fromId: string, toId: string): WatchlistViewItem[] {
  const fromIndex = items.findIndex((item) => item.id === fromId);
  const toIndex = items.findIndex((item) => item.id === toId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return items;

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return items;
  next.splice(toIndex, 0, moved);
  return next;
}

export function moveWatchlist(watchlists: ApiWatchlist[], fromId: string, toId: string): ApiWatchlist[] {
  const fromIndex = watchlists.findIndex((watchlist) => watchlist.id === fromId);
  const toIndex = watchlists.findIndex((watchlist) => watchlist.id === toId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return watchlists;

  const next = [...watchlists];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return watchlists;
  next.splice(toIndex, 0, moved);
  return next;
}

export function updateActiveWatchlistItems(watchlists: ApiWatchlist[], watchlistId: string, update: (items: WatchlistViewItem[]) => WatchlistViewItem[]): ApiWatchlist[] {
  return watchlists.map((watchlist) => watchlist.id === watchlistId
    ? { ...watchlist, items: update(toWatchlistViewItems(watchlist)) }
    : watchlist);
}

export function toWatchlistViewItems(watchlist: ApiWatchlist | null): WatchlistViewItem[] {
  if (!watchlist || !Array.isArray(watchlist.items)) return [];
  return watchlist.items.map((item) => ({
    ...item,
    symbolData: toWatchlistItemSymbol(item),
    isInactive: item.item_type === 'symbol' && item.symbol_detail?.is_active === false,
  }));
}

function toWatchlistItemSymbol(item: ApiWatchlist['items'][number]): ChartSymbol | undefined {
  if (item.item_type !== 'symbol') return undefined;
  if (item.symbol_detail) return normalizeSymbol(item.symbol_detail);
  if (!item.symbol) return undefined;

  return normalizeSymbol({
    symbol: item.symbol,
    name: item.name ?? item.symbol,
    description: item.name ?? item.symbol,
    exchange: 'Local',
    asset_type: null,
    price_scale: null,
  });
}

export function watchlistHasSymbol(watchlist: ApiWatchlist, symbol: ChartSymbol): boolean {
  return watchlist.items.some((item) => item.item_type === 'symbol'
    && (item.symbol === symbol.name
      || item.symbol_detail?.symbol === symbol.name
      || item.symbol_detail?.name === symbol.name));
}

export function normalizeWatchlists(value: ApiWatchlist[] | null | undefined): ApiWatchlist[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((watchlist) => Boolean(watchlist?.id))
    .map((watchlist) => ({
      ...watchlist,
      name: watchlist.name || 'Watchlist',
      is_pinned: Boolean(watchlist.is_pinned),
      items: Array.isArray(watchlist.items) ? watchlist.items : [],
    }));
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Watchlist update failed';
}

export function loadStoredActiveTab(): AppTab {
  const value = loadStoredJSON<string>(activeTabStorageKey, 'watchlist');
  return isAppTab(value) ? value : 'watchlist';
}

export function loadStoredActiveWatchlistId(): string | null {
	const value = loadStoredJSON<string | null>(activeWatchlistStorageKey, null);
	return typeof value === 'string' && value ? value : null;
}

export function loadStoredChartState(): StoredChartState | null {
  const value = loadStoredJSON<Partial<StoredChartState> | null>(chartStateStorageKey, null);
  if (!value || typeof value.symbol !== 'string' || !value.symbol) return null;
  return {
    symbol: value.symbol,
    interval: typeof value.interval === 'string' && value.interval ? value.interval : '1D',
  };
}

function isAppTab(value: string): value is AppTab {
  return value === 'watchlist'
    || value === 'chart'
    || value === 'news'
    || value === 'explore'
    || value === 'community'
    || value === 'menu';
}

export function getInitialChartState(layout: unknown, fallbackSymbol: string): { symbol: string; interval: string } {
  const mainState = findMainSeriesState(layout);
  return {
    symbol: typeof mainState?.symbol === 'string' && mainState.symbol ? mainState.symbol : fallbackSymbol,
    interval: typeof mainState?.interval === 'string' && mainState.interval ? mainState.interval : '1D',
  };
}

function findMainSeriesState(value: unknown): { symbol?: unknown; interval?: unknown } | null {
  if (!value || typeof value !== 'object') return null;

  if ('type' in value && 'state' in value) {
    const source = value as { type?: unknown; state?: unknown };
    if (source.type === 'MainSeries' && source.state && typeof source.state === 'object') {
      return source.state as { symbol?: unknown; interval?: unknown };
    }
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findMainSeriesState(item);
      if (found) return found;
    }
    return null;
  }

  for (const child of Object.values(value)) {
    const found = findMainSeriesState(child);
    if (found) return found;
  }

  return null;
}
